













































































































































































import { defineComponent, PropType, watch } from '@nuxtjs/composition-api';
import CartDiscounts from '@shared/components/CartDiscounts/CartDiscounts.vue';
import useBrandConfig from '@shared/composables/useBrandConfig';
import useCartCookie from '@shared/composables/useCartCookie';
import useFeatureFlag from '@shared/composables/useFeatureFlag';
import useLocalStorageCart from '@shared/composables/useLocalStorageCart';
import { isFreeWithDiscount } from '@shared/composables/useShopifyDiscounts/helpers';
import useUiNotification from '@shared/composables/useUiNotification';
import {
  getProductFailedToRemoveFromCartNotification,
  getProductFailedToUpdateCartNotification,
} from '@shared/composables/useUiNotification/commonNotifications';
import useUiState from '@shared/composables/useUiState';
import analytics from '@shared/helpers/analytics';
import { CSSClass } from '@shared/types/vue';
import {
  BaseButton,
  BaseCollectedProduct,
  BaseHeading,
  BaseImage,
  BasePrice,
  BaseProperty,
  BaseQuantitySelector,
  BaseSidebar,
  UCIcon,
} from '@shared/ui';
import { Cart, cartGetters, LineItem } from '@unified-commerce/gpc-vue-storefront-shopify';
import debounce from 'lodash/debounce';

import { HIDDEN_PRODUCT_TAG } from '~/constants';

export default defineComponent({
  name: 'CartSidebar',
  components: {
    CartDiscounts,
    BaseSidebar,
    BaseButton,
    BaseHeading,
    BaseProperty,
    BasePrice,
    BaseCollectedProduct,
    BaseImage,
    BaseQuantitySelector,
    UCIcon,
  },
  props: {
    cart: {
      type: Object as PropType<Cart>,
      default: () => null,
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props) {
    const { isCartSidebarOpen, toggleCartSidebar } = useUiState();
    const { globalConfig } = useBrandConfig();
    const { removeItem, updateItemQty, loading, error } = useCartCookie();
    const { send: sendUiNotification } = useUiNotification();
    const { setCartLocalStorage } = useLocalStorageCart();
    const { featureFlagEnabled } = useFeatureFlag();

    const handleCheckout = (checkoutUrl: string) => {
      window.location.href = checkoutUrl;
    };

    const isHiddenProduct = (lineItem: LineItem) =>
      (cartGetters.getProductTags(lineItem) ?? []).includes(HIDDEN_PRODUCT_TAG);

    const updateQuantity = debounce(async ({ product, quantity }) => {
      await updateItemQty({ product, quantity });
      if (error.value.updateItemQty) {
        sendUiNotification(getProductFailedToUpdateCartNotification());
      }
      setCartLocalStorage();
    }, 500);

    const removeItemFromCart = debounce(async ({ product }) => {
      await removeItem({ product });
      if (error.value.removeItem) {
        sendUiNotification(
          getProductFailedToRemoveFromCartNotification({
            name: cartGetters.getItemName(product),
          }),
        );
      }
      setCartLocalStorage();
    }, 500);

    watch(isCartSidebarOpen, () => {
      if (isCartSidebarOpen && isCartSidebarOpen.value === true) {
        analytics({
          event: 'view_cart',
          cart: props.cart,
        });
      }
    });

    return {
      globalConfig,
      updateQuantity,
      loading,
      removeItemFromCart,
      handleCheckout,
      isCartSidebarOpen,
      toggleCartSidebar,
      cartGetters,
      isHiddenProduct,
      isFreeWithDiscount,
      featureFlagEnabled,
    };
  },
  methods: {
    collectedProductClass(product: LineItem): CSSClass[] {
      const baseClass = 'collected-product';
      const noAttributesClass = `${baseClass}--no-attributes`;

      return [
        baseClass,
        {
          [noAttributesClass]: !this.showItemAttributes(product),
        },
      ];
    },
    showItemAttributes(product: LineItem): boolean {
      const attributes = Object.entries(cartGetters.getItemAttributes(product))[0];
      return !(attributes[0] === 'Title' && attributes[1] === 'Default Title');
    },
    calculateSavings(): number {
      let savings = 0;
      cartGetters.getItems(this.cart).forEach((product) => {
        if (cartGetters.getCompareToPrice(product)) {
          const qty = cartGetters.getCartItemQuantity(product);
          const regularPrice = cartGetters.getPrice(product);
          const specialPrice = cartGetters.getCompareToPrice(product);
          if (regularPrice && specialPrice) {
            savings += qty * (regularPrice - specialPrice);
          }
        }
      });

      return -1 * savings;
    },
  },
});
